import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Ban, CalendarClock, Check, Loader2, Pencil, Truck } from "lucide-react";

export function OrderDetailModals(props) {
  const {
    order,
    orderNum,
    deliverers,
    showSwapDialog,
    setShowSwapDialog,
    swapDeliverer,
    setSwapDeliverer,
    swapping,
    handleSwapDeliverer,
    showPaymentDialog,
    setShowPaymentDialog,
    paymentMethods,
    updatingPayment,
    handleChangePayment,
    showAddProduct,
    setShowAddProduct,
    productSearch,
    setProductSearch,
    menuProducts,
    addProductToOrder,
    formatPrice,
    showEditDialog,
    setShowEditDialog,
    editName,
    setEditName,
    editPhone,
    setEditPhone,
    isDelivery,
    editAddress,
    setEditAddress,
    editNotes,
    setEditNotes,
    savingEdit,
    handleSaveEdit,
    showScheduleDialog,
    setShowScheduleDialog,
    scheduleFor,
    setScheduleFor,
    savingSchedule,
    handleSaveSchedule,
    showCancelDialog,
    setShowCancelDialog,
    cancelReason,
    setCancelReason,
    handleCancelConfirm,
    cancelling,
  } = props;

  return (
    <>
      <Dialog open={showSwapDialog} onOpenChange={setShowSwapDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle className="uppercase font-bold">Alterar Entregador</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <p className="text-sm text-gray-500">Entregador actual: <span className="font-bold text-gray-700">{deliverers.find(d => d.id === order.deliverer_id)?.name || "-"}</span></p>
            <Select value={swapDeliverer} onValueChange={setSwapDeliverer}>
              <SelectTrigger data-testid="select-swap-deliverer"><SelectValue placeholder="Novo entregador..." /></SelectTrigger>
              <SelectContent position="popper" sideOffset={4}>
                {deliverers.filter(d => d.id !== order.deliverer_id && (d.status === "available" || d.active !== false)).map(d => (
                  <SelectItem key={d.id} value={d.id}>{d.name} ({d.vehicle}) - {d.status === "available" ? "Disponible" : d.status}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowSwapDialog(false)}>Cancelar</Button>
              <Button className="bg-emerald-600 hover:bg-emerald-700 gap-2" onClick={handleSwapDeliverer} disabled={swapping || !swapDeliverer} data-testid="confirm-swap-btn">
                {swapping ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Truck className="w-4 h-4" /> Cambiar</>}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle className="uppercase font-bold">Forma de Pago</DialogTitle></DialogHeader>
          <div className="space-y-2 mt-2">
            <p className="text-sm text-gray-500 mb-3">Metodo actual: <span className="font-bold text-gray-700">{order.payment_method || "Sin definir"}</span></p>
            {paymentMethods.map((m, i) => (
              <Button key={i} variant="outline" className={`w-full justify-start gap-3 h-12 ${order.payment_method === m.name ? "border-emerald-500 bg-emerald-50" : ""}`}
                onClick={() => handleChangePayment(m.name)} disabled={updatingPayment} data-testid={`payment-method-${i}`}>
                <span className="font-medium">{m.name}</span>
                {m.pct > 0 && <span className="text-xs text-gray-400 ml-auto">{m.pct}%</span>}
              </Button>
            ))}
            {paymentMethods.length === 0 && <p className="text-sm text-gray-400 text-center py-4">No hay metodos de pago configurados. Configure en Settings &rarr; Pagos</p>}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showAddProduct} onOpenChange={setShowAddProduct}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="uppercase font-bold">Adicionar Item</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <Input placeholder="Buscar producto..." value={productSearch} onChange={e => setProductSearch(e.target.value)} data-testid="search-product-input" />
            <div className="h-60 overflow-y-auto">
              <div className="space-y-1">
                {menuProducts.filter(p => !productSearch || p.name.toLowerCase().includes(productSearch.toLowerCase())).map(p => (
                  <button key={p.id} className="w-full text-left px-3 py-2 rounded-lg hover:bg-emerald-50 flex items-center justify-between transition-colors" onClick={() => addProductToOrder(p)} data-testid={`add-product-${p.id?.slice(-6)}`}>
                    <span className="text-sm font-medium text-gray-900 truncate">{p.name}</span>
                    <span className="text-sm font-bold text-emerald-600 shrink-0 ml-2">{formatPrice(p.price)}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Pencil className="w-4 h-4" />Editar Pedido #{orderNum}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-1">
            <div><label className="text-xs font-semibold text-gray-500 uppercase block mb-1">Nome do cliente</label><Input value={editName} onChange={e => setEditName(e.target.value)} placeholder="Nome" data-testid="edit-name-input" /></div>
            <div><label className="text-xs font-semibold text-gray-500 uppercase block mb-1">Telefone</label><Input value={editPhone} onChange={e => setEditPhone(e.target.value)} placeholder="+351..." data-testid="edit-phone-input" /></div>
            {isDelivery && <div><label className="text-xs font-semibold text-gray-500 uppercase block mb-1">Morada</label><Input value={editAddress} onChange={e => setEditAddress(e.target.value)} placeholder="Rua, número..." data-testid="edit-address-input" /></div>}
            <div><label className="text-xs font-semibold text-gray-500 uppercase block mb-1">Observações</label><Input value={editNotes} onChange={e => setEditNotes(e.target.value)} placeholder="Obs. do pedido..." data-testid="edit-notes-input" /></div>
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancelar</Button>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white gap-1" onClick={handleSaveEdit} disabled={savingEdit} data-testid="confirm-edit-btn">{savingEdit ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}Guardar</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showScheduleDialog} onOpenChange={setShowScheduleDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><CalendarClock className="w-4 h-4" />{order.scheduled_for ? "Reagendar" : "Agendar"} Pedido #{orderNum}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-1">
            <label className="text-xs font-semibold text-gray-500 uppercase block mb-1">Data e hora</label>
            <Input type="datetime-local" value={scheduleFor} onChange={e => setScheduleFor(e.target.value)} data-testid="schedule-datetime-input" />
            {order.scheduled_for && (<p className="text-xs text-gray-400">Agendado actualmente: {new Date(order.scheduled_for).toLocaleString("pt-PT")}</p>)}
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setShowScheduleDialog(false)}>Cancelar</Button>
            <Button className="bg-orange-500 hover:bg-orange-600 text-white gap-1" onClick={handleSaveSchedule} disabled={savingSchedule} data-testid="confirm-schedule-btn">{savingSchedule ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarClock className="w-4 h-4" />}Agendar</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="text-red-700 flex items-center gap-2"><Ban className="w-5 h-5" />Cancelar Pedido #{orderNum}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-1">
            <p className="text-sm text-gray-600">Este pedido será marcado como <strong>cancelado</strong> e removido do caixa.{order.status === "delivered" && <span className="block mt-1 text-amber-700 font-semibold">⚠️ Este pedido já foi entregue.</span>}</p>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase block mb-1">Motivo (opcional)</label>
              <Input value={cancelReason} onChange={e => setCancelReason(e.target.value)} placeholder="Ex: erro no pedido, cliente desistiu..." data-testid="cancel-reason-input" onKeyDown={e => e.key === "Enter" && handleCancelConfirm()} autoFocus />
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setShowCancelDialog(false)}>Não cancelar</Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white gap-1" onClick={handleCancelConfirm} data-testid="confirm-cancel-btn" disabled={cancelling}>{cancelling ? <Loader2 className="w-4 h-4 animate-spin" /> : <Ban className="w-4 h-4" />}Cancelar pedido</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
